import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import InvoiceTable from './InvoiceTables';

function ReferralTable({ data, setIsLoading, isLoading, filters, flow }) {
    const [currentPage, setCurrentPage] = useState(1);
    const [recordsPerPage, setRecordsPerPage] = useState(10);
    const [paginatedData, setPaginatedData] = useState([]);
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [selectedDoctor, setSelectedDoctor] = useState(null);

    // Handle pagination
    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleRecordsPerPageChange = (e) => {
        setRecordsPerPage(Number(e.target.value));
        setCurrentPage(1); // Reset to first page on change
    };

    // Get the current records for the page
    useEffect(() => {
        setIsLoading(true);
        const indexOfLastRecord = currentPage * recordsPerPage;
        const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
        const currentRecords = data?.slice(indexOfFirstRecord, indexOfLastRecord);
        setPaginatedData(currentRecords);
        setIsLoading(false);
    }, [currentPage, recordsPerPage, data]);

    // Fetch data for InvoiceTable when a doctor is selected
    const fetchInvoiceData = async (doctor) => {
        if (flow != "surgerypackage") {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/mis-invoice/list`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    location_id: doctor.CenterId.toString(),
                    defination: doctor['Doctor Name'],
                    startDate: filters?.startDate,
                    endDate: filters?.endDate
                })
            });

            const result = await response.json();
            setSelectedDoctor({ ...doctor, invoiceData: result });
            setIsPopupOpen(true);
        }
        else{
            const response = await fetch(`${process.env.REACT_APP_API_URL}/mis-invoice/list/surgery-package`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    location_id: doctor.CenterId.toString(),
                    defination: doctor['Doctor Name'],
                    startDate: filters?.startDate,
                    endDate: filters?.endDate
                })
            });

            const result = await response.json();
            setSelectedDoctor({ ...doctor, invoiceData: result });
            setIsPopupOpen(true);
        }

    };

    // Calculate total pages
    const totalPages = Math.ceil(data?.length / recordsPerPage);

    return (
        <div>
            <div style={{ marginBottom: '10px' }}>
                <label>Records per page: </label>
                <select onChange={handleRecordsPerPageChange} value={recordsPerPage}>
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={data?.length}>All</option>
                </select>
            </div>

            {/* Table */}
            <table
                border="1"
                style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    overflow: 'scroll',
                    backgroundColor: '#f1f9ff',
                    borderRadius: '8px',
                    boxShadow: '0px 0px 5px rgba(0, 0, 0, 0.1)',
                }}
            >
                <thead>
                    <tr style={{ backgroundColor: '#003366', color: '#fff' }}>
                        <th>Referral Doctor Name</th>
                        <th>Center</th>
                        <th>Primary Doctor</th>
                        <th>Patient Name</th>
                        <th>Admitted On</th>
                        <th>Discharged On</th>
                        <th>Net Invoice Value</th>
                        <th>TPA</th>
                        <th>Paid</th>
                        <th>Unpaid</th>
                    </tr>
                </thead>
                <tbody>
                    {isLoading ? (
                        <tr>
                            <td colSpan="11" style={{ textAlign: 'center' }}>
                                <span>Loading...</span>
                            </td>
                        </tr>
                    ) : paginatedData?.length > 0 ? (
                        paginatedData.map((item, index) => (
                            <tr
                                key={item.ipdId}
                                style={{
                                    backgroundColor: index % 2 === 0 ? '#e6f0ff' : '#cce0ff', // Dual-tone styling
                                }}
                            >
                                <td>{item['referred_by_doctor']}</td>
                                <td>{item['center']}</td>
                                <td>{item['admitted_to_doctor']}</td>
                                <td>{item['patient_name']}</td>
                                <td>{new Date(item['admitted_at']).toLocaleDateString()}</td>
                                <td>{new Date(item['discharged_at']).toLocaleDateString()}</td>
                                <td>{item['grand_total'].toFixed(2)}</td>
                                <td>{item['tpa']}</td>
                                <td>{item['total_collected'].toFixed(2)}</td>
                                <td>{Math.max(0, (item['grand_total'] - item['total_collected'])).toFixed(2)}</td>
                            
                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td colSpan="11" style={{ textAlign: 'center' }}>
                                No data available
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>

            {/* Pagination Controls */}
            {totalPages > 1 && (
                <div style={{ textAlign: 'center', marginTop: '20px' }}>
                    <button
                        onClick={() => handlePageChange(1)}
                        disabled={currentPage === 1}
                    >
                        First
                    </button>
                    <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                    >
                        Prev
                    </button>
                    {[...Array(totalPages).keys()].map((page) => (
                        <button
                            key={page + 1}
                            onClick={() => handlePageChange(page + 1)}
                            disabled={currentPage === page + 1}
                            style={{
                                backgroundColor: currentPage === page + 1 ? '#003366' : '#e6e6e6',
                                color: currentPage === page + 1 ? '#fff' : '#000',
                                border: 'none',
                                margin: '0 5px',
                                padding: '5px 10px',
                                cursor: 'pointer',
                            }}
                        >
                            {page + 1}
                        </button>
                    ))}
                    <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                    >
                        Next
                    </button>
                    <button
                        onClick={() => handlePageChange(totalPages)}
                        disabled={currentPage === totalPages}
                    >
                        Last
                    </button>
                </div>
            )}

            {/* Popup Dialog for InvoiceTable */}
            <Dialog
                open={isPopupOpen}
                onClose={() => setIsPopupOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Invoice Details</DialogTitle>
                <DialogContent>
                    {selectedDoctor && (
                        <InvoiceTable
                            data={selectedDoctor.invoiceData}
                            setIsLoading={setIsLoading}
                            isLoading={isLoading}
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsPopupOpen(false)} color="primary">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default ReferralTable;
